import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut, Download, Trash2, Mail, Phone, Building2, IndianRupee, Package,
  Plus, Edit3, X, Loader2, Filter, ShoppingBag, Inbox, ShieldCheck, RefreshCw,
  Server, FileArchive, Image as ImageIcon, Award, Settings, UploadCloud
} from 'lucide-react';
import {
  adminMe, adminListEnquiries, adminDeleteEnquiry, adminUpdateEnquiryStatus, adminExportUrl,
  adminListProducts, adminCreateProduct, adminUpdateProduct, adminDeleteProduct, API,
  adminUploadImage,
} from '../lib/api';
import { toast } from '../hooks/use-toast';
import { LOGO_URL } from '../mock/mock';
import BannersTab from './admin/BannersTab';
import LogosTab from './admin/LogosTab';
import SettingsTab from './admin/SettingsTab';

const STATUSES = ['new', 'contacted', 'quoted', 'closed', 'lost'];
const SECTIONS = ['featured', 'top-selling', 'diwali'];

const Stat = ({ label, value, icon: Icon }) => (
  <div className="bg-white rounded-2xl border border-[#EFE3D2] p-5">
    <div className="flex items-center gap-3">
      <div className="w-11 h-11 rounded-xl pastel-blush flex items-center justify-center">
        <Icon className="w-5 h-5 text-[#7A5C4C]" />
      </div>
      <div>
        <div className="text-xs uppercase tracking-widest text-[#7A5C4C]">{label}</div>
        <div className="font-serif-display text-2xl text-[#2C2416]">{value}</div>
      </div>
    </div>
  </div>
);

const StatusPill = ({ value, onChange }) => (
  <select value={value} onChange={(e) => onChange(e.target.value)}
    className="text-[11px] uppercase tracking-widest bg-[#FAF6F1] border border-[#EFE3D2] rounded-full px-3 py-1.5 focus:outline-none focus:border-[#C58B76]">
    {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
  </select>
);

const EnquiriesTab = ({ enquiries, refresh }) => {
  const [filter, setFilter] = useState('all');
  const [q, setQ] = useState('');

  const filtered = enquiries.filter(e => {
    const matchStatus = filter === 'all' || e.status === filter;
    const s = q.toLowerCase();
    const matchSearch = !s || [e.name, e.email, e.phone, e.company].join(' ').toLowerCase().includes(s);
    return matchStatus && matchSearch;
  });

  const download = async () => {
    const token = localStorage.getItem('wyw_admin_token');
    const res = await fetch(adminExportUrl(), { headers: { Authorization: `Bearer ${token}` } });
    if (!res.ok) return toast({ title: 'Export failed', variant: 'destructive' });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `wyw-enquiries-${Date.now()}.xlsx`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
    toast({ title: 'Excel downloaded' });
  };

  const setStatus = async (id, s) => {
    try {
      await adminUpdateEnquiryStatus(id, s);
      toast({ title: 'Status updated' });
      refresh();
    } catch (e) { toast({ title: 'Failed', variant: 'destructive' }); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this enquiry permanently?')) return;
    await adminDeleteEnquiry(id);
    toast({ title: 'Deleted' });
    refresh();
  };

  return (
    <div>
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between mb-6">
        <div className="flex items-center gap-2 flex-1">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone..."
            className="w-full md:max-w-sm bg-white border border-[#EFE3D2] rounded-full px-4 py-2 text-sm focus:outline-none focus:border-[#C58B76]" />
          <div className="flex items-center gap-2 bg-white border border-[#EFE3D2] rounded-full px-3 py-1.5">
            <Filter className="w-3.5 h-3.5 text-[#7A5C4C]" />
            <select value={filter} onChange={(e) => setFilter(e.target.value)}
              className="text-xs uppercase tracking-widest bg-transparent focus:outline-none">
              <option value="all">All</option>
              {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
        </div>
        <button onClick={download} className="btn-primary rounded-full px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2">
          <Download className="w-4 h-4" /> Export Excel
        </button>
      </div>

      <div className="bg-white rounded-2xl border border-[#EFE3D2] overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[900px]">
            <thead className="bg-[#FAF6F1] text-left text-[10px] uppercase tracking-widest text-[#7A5C4C]">
              <tr>
                <th className="px-4 py-3">Received</th>
                <th className="px-4 py-3">Contact</th>
                <th className="px-4 py-3">Company</th>
                <th className="px-4 py-3">Budget · Qty</th>
                <th className="px-4 py-3">Source</th>
                <th className="px-4 py-3">Message</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#EFE3D2]">
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-[#7A5C4C]">No enquiries yet.</td></tr>
              )}
              {filtered.map(e => (
                <tr key={e.id} className="hover:bg-[#FAF6F1] transition-colors">
                  <td className="px-4 py-3 text-[#5B4A3A] whitespace-nowrap">{new Date(e.created_at).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' })}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-[#2C2416]">{e.name}</div>
                    <div className="text-xs text-[#7A5C4C] flex items-center gap-1"><Mail className="w-3 h-3" /> {e.email}</div>
                    <div className="text-xs text-[#7A5C4C] flex items-center gap-1"><Phone className="w-3 h-3" /> {e.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-[#5B4A3A]">{e.company || '—'}</td>
                  <td className="px-4 py-3 text-[#5B4A3A]">
                    <div>{e.budget || '—'}</div>
                    <div className="text-xs text-[#7A5C4C]">{e.quantity ? `${e.quantity} hampers` : ''}</div>
                  </td>
                  <td className="px-4 py-3"><span className="text-[10px] uppercase tracking-widest bg-[#F4E7DA] px-2 py-1 rounded-full">{e.source}</span></td>
                  <td className="px-4 py-3 text-[#5B4A3A] max-w-[240px] truncate" title={e.message}>{e.message || '—'}</td>
                  <td className="px-4 py-3"><StatusPill value={e.status} onChange={(v) => setStatus(e.id, v)} /></td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => remove(e.id)} className="text-[#7A5C4C] hover:text-red-600 p-1"><Trash2 className="w-4 h-4" /></button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const ProductForm = ({ initial, onSave, onCancel }) => {
  const [f, setF] = useState(initial || { name: '', category: '', price: 0, img: '', img2: '', tag: '', description: '', section: 'featured', active: true });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(null); // 'img' | 'img2' | null
  const set = (k, v) => setF({ ...f, [k]: v });

  const upload = async (field, file) => {
    if (!file) return;
    setUploading(field);
    try {
      const res = await adminUploadImage(file, 'products');
      set(field, res.url);
      toast({ title: 'Image uploaded' });
    } catch (e) {
      toast({ title: 'Upload failed', description: e?.response?.data?.detail || '', variant: 'destructive' });
    } finally { setUploading(null); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await onSave({ ...f, price: Number(f.price) || 0 });
    } finally {
      setSaving(false);
    }
  };

  const inputCls = 'w-full bg-white border border-[#EFE3D2] rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#C58B76]';

  const ImageInput = ({ field, label, required }) => (
    <div className="md:col-span-2">
      <label className="text-xs uppercase tracking-widest text-[#7A5C4C]">{label}{required && ' *'}</label>
      <div className="flex gap-2 mt-1">
        <input required={required} className={inputCls} value={f[field]} onChange={(e) => set(field, e.target.value)} placeholder="Paste URL or upload" />
        <label className="btn-outline whitespace-nowrap rounded-lg px-3 text-xs uppercase tracking-widest inline-flex items-center gap-1 cursor-pointer">
          {uploading === field ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <UploadCloud className="w-3.5 h-3.5" />} Upload
          <input type="file" hidden accept="image/*" onChange={(e) => upload(field, e.target.files?.[0])} />
        </label>
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
      <form onSubmit={submit} className="bg-white rounded-2xl w-full max-w-2xl p-6 md:p-8 my-8">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-2xl text-[#2C2416]">{initial ? 'Edit hamper' : 'Add hamper'}</h3>
          <button type="button" onClick={onCancel} className="p-1 hover:bg-[#FAF6F1] rounded-full"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid md:grid-cols-2 gap-3">
          <div className="md:col-span-2"><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Name *</label><input required className={inputCls} value={f.name} onChange={(e) => set('name', e.target.value)} /></div>
          <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Category *</label><input required className={inputCls} value={f.category} onChange={(e) => set('category', e.target.value)} placeholder="e.g. Employee Onboarding" /></div>
          <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Internal Price (₹) — hidden from website</label><input type="number" min="0" className={inputCls} value={f.price} onChange={(e) => set('price', e.target.value)} placeholder="Optional" /></div>
          <ImageInput field="img" label="Image URL" required />
          <ImageInput field="img2" label="Hover Image URL (optional)" />
          <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Tag</label><input className={inputCls} value={f.tag} onChange={(e) => set('tag', e.target.value)} placeholder="Best Seller / New / Premium" /></div>
          <div><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Section</label>
            <select className={inputCls} value={f.section} onChange={(e) => set('section', e.target.value)}>
              {SECTIONS.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="md:col-span-2"><label className="text-xs uppercase tracking-widest text-[#7A5C4C]">Description</label><textarea rows="3" className={inputCls} value={f.description} onChange={(e) => set('description', e.target.value)} /></div>
          <label className="md:col-span-2 flex items-center gap-2 text-sm text-[#2C2416]">
            <input type="checkbox" checked={f.active} onChange={(e) => set('active', e.target.checked)} /> Active (shown on website)
          </label>
        </div>

        {f.img && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-widest text-[#7A5C4C] mb-2">Preview</div>
            <div className="aspect-[4/5] max-w-[180px] rounded-xl overflow-hidden border border-[#EFE3D2]">
              <img src={f.img.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${f.img}` : f.img} alt="preview" className="w-full h-full object-cover" onError={(e) => e.currentTarget.style.opacity = 0.3} />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-6">
          <button type="button" onClick={onCancel} className="btn-outline rounded-full px-5 py-2 text-xs uppercase tracking-widest">Cancel</button>
          <button type="submit" disabled={saving} className="btn-primary rounded-full px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2 disabled:opacity-60">
            {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving</> : 'Save hamper'}
          </button>
        </div>
      </form>
    </div>
  );
};

const ProductsTab = ({ products, refresh }) => {
  const [editing, setEditing] = useState(null); // null | product object | 'new'

  const save = async (data) => {
    try {
      if (editing === 'new') {
        await adminCreateProduct(data);
        toast({ title: 'Hamper added' });
      } else {
        await adminUpdateProduct(editing.id, data);
        toast({ title: 'Hamper updated' });
      }
      setEditing(null);
      refresh();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || 'Try again', variant: 'destructive' });
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this hamper?')) return;
    await adminDeleteProduct(id);
    toast({ title: 'Deleted' });
    refresh();
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <p className="text-sm text-[#5B4A3A]">{products.length} hamper{products.length !== 1 && 's'} · shown on website when active</p>
        <button onClick={() => setEditing('new')} className="btn-primary rounded-full px-5 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2">
          <Plus className="w-4 h-4" /> Add hamper
        </button>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {products.length === 0 && (
          <div className="col-span-full bg-white rounded-2xl border border-[#EFE3D2] p-10 text-center">
            <Package className="w-8 h-8 mx-auto text-[#C58B76]" />
            <p className="mt-3 text-[#5B4A3A]">No hampers yet. Click “Add hamper” to publish your first one.</p>
          </div>
        )}
        {products.map(p => (
          <div key={p.id} className="bg-white rounded-2xl border border-[#EFE3D2] overflow-hidden group">
            <div className="aspect-[4/5] bg-[#FAF6F1] overflow-hidden">
              <img src={p.img?.startsWith('/api/') ? `${process.env.REACT_APP_BACKEND_URL}${p.img}` : p.img} alt={p.name} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            </div>
            <div className="p-4">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full ${p.active ? 'bg-[#C8D5B9] text-[#3A4A2B]' : 'bg-[#F4D5C5] text-[#7A5C4C]'}`}>
                  {p.active ? 'Live' : 'Hidden'}
                </span>
                <span className="text-[10px] uppercase tracking-widest text-[#7A5C4C]">{p.section}</span>
              </div>
              <div className="font-serif-display text-lg text-[#2C2416] mt-1 leading-snug">{p.name}</div>
              <div className="text-xs text-[#7A5C4C]">{p.category}</div>
              <div className="flex justify-between mt-3">
                <button onClick={() => setEditing(p)} className="text-xs uppercase tracking-widest text-[#2C2416] hover:text-[#C58B76] inline-flex items-center gap-1"><Edit3 className="w-3.5 h-3.5" /> Edit</button>
                <button onClick={() => remove(p.id)} className="text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-red-600 inline-flex items-center gap-1"><Trash2 className="w-3.5 h-3.5" /> Delete</button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && <ProductForm initial={editing === 'new' ? null : editing} onSave={save} onCancel={() => setEditing(null)} />}
    </div>
  );
};

const DeployTab = () => {
  const downloadUrl = `${API}/download/hostinger-package`;
  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 bg-white rounded-2xl border border-[#EFE3D2] p-6 md:p-8">
        <div className="flex items-center gap-3 mb-2">
          <FileArchive className="w-6 h-6 text-[#C58B76]" />
          <h3 className="font-serif-display text-2xl text-[#2C2416]">Hostinger VPS package</h3>
        </div>
        <p className="text-sm text-[#5B4A3A] leading-relaxed">
          Ready-to-deploy zip with the full source code, Nginx config, systemd service, MongoDB backup script, and a step-by-step README for your Hostinger VPS.
        </p>

        <div className="mt-5 flex flex-wrap gap-3">
          <a href={downloadUrl} target="_blank" rel="noreferrer"
            className="btn-primary rounded-full px-6 py-3 text-xs uppercase tracking-widest inline-flex items-center gap-2">
            <Download className="w-4 h-4" /> Download wyw-hostinger.zip
          </a>
          <a href="https://hpanel.hostinger.com" target="_blank" rel="noreferrer"
            className="btn-outline rounded-full px-6 py-3 text-xs uppercase tracking-widest">
            Open Hostinger Panel
          </a>
        </div>

        <div className="mt-8 space-y-4">
          <h4 className="text-xs uppercase tracking-widest text-[#7A5C4C]">Quick start</h4>
          <ol className="space-y-3 text-sm text-[#2C2416]">
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#F4D5C5] text-[#2C2416] flex items-center justify-center text-xs font-medium flex-shrink-0">1</span> Download the zip and extract it locally.</li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#F4D5C5] text-[#2C2416] flex items-center justify-center text-xs font-medium flex-shrink-0">2</span> On your Hostinger VPS (Ubuntu 22.04), run <code className="bg-[#FAF6F1] px-2 py-0.5 rounded text-xs">bash deploy/bootstrap.sh</code> to install Node, Python, MongoDB, Nginx.</li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#F4D5C5] text-[#2C2416] flex items-center justify-center text-xs font-medium flex-shrink-0">3</span> Upload the folder to <code className="bg-[#FAF6F1] px-2 py-0.5 rounded text-xs">/opt/wyw-backend</code> and <code className="bg-[#FAF6F1] px-2 py-0.5 rounded text-xs">/opt/wyw-frontend</code>.</li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#F4D5C5] text-[#2C2416] flex items-center justify-center text-xs font-medium flex-shrink-0">4</span> Copy env examples, edit passwords + domain.</li>
            <li className="flex gap-3"><span className="w-6 h-6 rounded-full bg-[#F4D5C5] text-[#2C2416] flex items-center justify-center text-xs font-medium flex-shrink-0">5</span> Enable Nginx config, run <code className="bg-[#FAF6F1] px-2 py-0.5 rounded text-xs">certbot --nginx</code> for free SSL, and you're live.</li>
          </ol>
          <p className="text-xs text-[#7A5C4C]">Full guide: see <code className="bg-[#FAF6F1] px-2 py-0.5 rounded">deploy/README.md</code> inside the zip.</p>
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-[#EFE3D2] p-6 md:p-8">
        <div className="text-xs uppercase tracking-widest text-[#C58B76] mb-2">What's included</div>
        <ul className="text-sm text-[#2C2416] space-y-2.5">
          <li className="flex gap-2"><Package className="w-4 h-4 mt-0.5 text-[#C58B76]" /> Full source (backend + frontend)</li>
          <li className="flex gap-2"><Server className="w-4 h-4 mt-0.5 text-[#C58B76]" /> One-shot Ubuntu bootstrap script</li>
          <li className="flex gap-2"><ShieldCheck className="w-4 h-4 mt-0.5 text-[#C58B76]" /> Nginx + SSL config (Let's Encrypt)</li>
          <li className="flex gap-2"><RefreshCw className="w-4 h-4 mt-0.5 text-[#C58B76]" /> systemd service (auto-restart)</li>
          <li className="flex gap-2"><Download className="w-4 h-4 mt-0.5 text-[#C58B76]" /> Daily MongoDB backup script</li>
          <li className="flex gap-2"><FileArchive className="w-4 h-4 mt-0.5 text-[#C58B76]" /> Step-by-step README (deploy/README.md)</li>
        </ul>
        <div className="mt-6 pt-6 border-t border-[#F0E7DA]">
          <div className="text-xs uppercase tracking-widest text-[#7A5C4C]">Recommended VPS</div>
          <div className="font-serif-display text-lg text-[#2C2416] mt-1">Hostinger KVM 2</div>
          <div className="text-xs text-[#7A5C4C] mt-1">2 vCPU · 8 GB RAM · 100 GB NVMe</div>
        </div>
      </div>
    </div>
  );
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('enquiries');
  const [enquiries, setEnquiries] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      await adminMe();
      const [e, p] = await Promise.all([adminListEnquiries(), adminListProducts()]);
      setEnquiries(e);
      setProducts(p);
    } catch (err) {
      localStorage.removeItem('wyw_admin_token');
      navigate('/admin/login');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!localStorage.getItem('wyw_admin_token')) return navigate('/admin/login');
    load();
    // eslint-disable-next-line
  }, []);

  const logout = () => {
    localStorage.removeItem('wyw_admin_token');
    localStorage.removeItem('wyw_admin_email');
    navigate('/admin/login');
  };

  const stats = {
    total: enquiries.length,
    newCount: enquiries.filter(e => e.status === 'new').length,
    products: products.filter(p => p.active).length,
    contacted: enquiries.filter(e => ['contacted', 'quoted'].includes(e.status)).length,
  };

  if (loading) return (
    <div className="min-h-screen bg-[#FAF6F1] flex items-center justify-center">
      <Loader2 className="w-6 h-6 animate-spin text-[#C58B76]" />
    </div>
  );

  return (
    <main className="min-h-screen bg-[#FAF6F1]">
      <header className="bg-white border-b border-[#EFE3D2] sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <img src={LOGO_URL} alt="WYW" className="h-10 w-auto" />
            <div className="hidden md:block">
              <div className="text-xs uppercase tracking-widest text-[#7A5C4C]">Admin Console</div>
              <div className="font-serif-display text-lg text-[#2C2416] leading-none">Wrap Your Wish</div>
            </div>
          </Link>
          <div className="flex items-center gap-3">
            <button onClick={load} className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-[#C58B76]"><RefreshCw className="w-4 h-4" /> Refresh</button>
            <button onClick={logout} className="btn-outline rounded-full px-4 py-2 text-xs uppercase tracking-widest inline-flex items-center gap-2">
              <LogOut className="w-4 h-4" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center gap-2 mb-6">
          <ShieldCheck className="w-5 h-5 text-[#C58B76]" />
          <h1 className="font-serif-display text-3xl md:text-4xl text-[#2C2416]">Dashboard</h1>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Stat label="Total enquiries" value={stats.total} icon={Inbox} />
          <Stat label="New (unattended)" value={stats.newCount} icon={Mail} />
          <Stat label="In progress" value={stats.contacted} icon={IndianRupee} />
          <Stat label="Active hampers" value={stats.products} icon={ShoppingBag} />
        </div>

        <div className="flex gap-2 mb-6 bg-white p-1 rounded-full border border-[#EFE3D2] w-fit overflow-x-auto max-w-full">
          {[
            { id: 'enquiries', label: 'Enquiries', icon: Inbox },
            { id: 'products', label: 'Hampers', icon: Package },
            { id: 'banners', label: 'Banners', icon: ImageIcon },
            { id: 'logos', label: 'Client Logos', icon: Award },
            { id: 'settings', label: 'Settings', icon: Settings },
            { id: 'deploy', label: 'Deploy', icon: Server },
          ].map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={`inline-flex items-center gap-2 px-5 py-2 text-xs uppercase tracking-widest rounded-full transition-colors ${tab === t.id ? 'bg-[#2C2416] text-[#F4D5C5]' : 'text-[#2C2416] hover:bg-[#FAF6F1]'}`}>
              <t.icon className="w-4 h-4" /> {t.label}
            </button>
          ))}
        </div>

        {tab === 'enquiries' && <EnquiriesTab enquiries={enquiries} refresh={load} />}
        {tab === 'products' && <ProductsTab products={products} refresh={load} />}
        {tab === 'banners' && <BannersTab />}
        {tab === 'logos' && <LogosTab />}
        {tab === 'settings' && <SettingsTab />}
        {tab === 'deploy' && <DeployTab />}
      </div>
    </main>
  );
};

export default AdminDashboard;
